import React, { useEffect, useRef, useState } from 'react';
import { FiMoreVertical } from 'react-icons/fi';
import Button from './Button';

// Primary-action tone → Button variant. Button (see Button.js) only ships three
// colored fills — primary(accent), success(green), danger(red) — plus neutrals.
// blue/amber have no dedicated fill, so they fall back to the accent primary;
// add Button variants there if the design ever needs distinct fills.
const TONE_TO_VARIANT = {
  accent: 'primary',
  blue: 'primary',
  green: 'success',
  amber: 'primary',
  red: 'danger',
};

// Primary size → Button size. Overflow trigger + menu items stay 44px regardless
// (touch targets); the labeled primary follows the requested density.
const PRIMARY_SIZE = { sm: 'sm', md: 'md' };

/**
 * Single-action row pattern: ONE labeled primary action + the rest tucked behind
 * an overflow (⋯) menu. Built for clickable table rows — clicks are stopped from
 * bubbling to the row by default (stopPropagation).
 *
 * primary?: { key, label, icon?, onClick, tone?, disabled?, disabledReason? }
 * items:    [{ key, label, icon?, onClick, destructive?, disabled?, disabledReason?, hidden? }]
 * menuLabel: aria-label for the icon-only overflow trigger — pages pass t(...)
 *   (no i18n in primitives). Icon-only buttons need an accessible name.
 */
export default function RowActions({ primary, items = [], size = 'sm', stopPropagation = true, menuLabel }) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const triggerRef = useRef(null);

  const visibleItems = items.filter((item) => !item.hidden);

  useEffect(() => {
    if (!open) return;
    const onOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, [open]);

  const handleKeyDown = (e) => {
    if (e.key === 'Escape' && open) {
      e.preventDefault();
      setOpen(false);
      triggerRef.current?.focus();
    }
  };

  const runItem = (item) => {
    setOpen(false);
    item.onClick?.();
  };

  return (
    <div
      ref={containerRef}
      className="inline-flex items-center gap-1"
      onClick={stopPropagation ? (e) => e.stopPropagation() : undefined}
      onKeyDown={handleKeyDown}
    >
      {primary && (
        <Button
          size={PRIMARY_SIZE[size] || PRIMARY_SIZE.sm}
          variant={TONE_TO_VARIANT[primary.tone] || 'primary'}
          icon={primary.icon}
          onClick={primary.onClick}
          disabled={primary.disabled}
          title={primary.disabled ? primary.disabledReason : undefined}
        >
          {primary.label}
        </Button>
      )}

      {visibleItems.length > 0 && (
        <div className="relative">
          <button
            type="button"
            ref={triggerRef}
            onClick={() => setOpen((o) => !o)}
            aria-label={menuLabel}
            aria-haspopup="menu"
            aria-expanded={open}
            className="inline-flex items-center justify-center min-w-[44px] min-h-[44px] rounded-lg text-content-secondary hover:bg-surface-2 hover:text-content transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <FiMoreVertical size={18} className="shrink-0" />
          </button>

          {open && (
            <div
              role="menu"
              className="absolute end-0 mt-1 z-20 min-w-[11rem] py-1 bg-surface border border-edge rounded-xl shadow-lg"
            >
              {visibleItems.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.key}
                    type="button"
                    role="menuitem"
                    disabled={item.disabled}
                    title={item.disabled ? item.disabledReason : undefined}
                    onClick={() => runItem(item)}
                    className={`w-full min-h-[44px] px-3 flex items-center gap-2.5 text-sm text-start transition-colors disabled:opacity-50 disabled:pointer-events-none hover:bg-surface-2 ${
                      item.destructive ? 'text-red-600 dark:text-red-400' : 'text-content-secondary'
                    }`}
                  >
                    {Icon && <Icon size={16} className="shrink-0" />}
                    <span className="truncate">{item.label}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
